import React from 'react';
import { Route } from 'react-router-dom';

import { ACTION, SECTION } from '@hooks/useDocumentTitle/constants';
import { definePageContextRoute, subjectResource } from '@context/pageContext';
import { botManagementRoutes } from '@components/botManagement/routes';
import { eventBlockingRoutes } from '@components/eventBlocking/routes';
import { dataMapperRoutes } from '@components/dataMapper/routes';
import { trackingPlansRoutes } from '@components/trackingPlans/routes';
import { audienceCohortRoutes } from '@components/cohorts/routes';
import { dataGraphAudienceRoutes } from '@components/dataGraphAudiences/routes';
import { profilesRoutes } from '@components/profiles/routes';
import { accessManagementRoutes } from '@components/accessManagement/routes';
import { retlWorkflowRoutes } from '@components/retlWorkflow/routes';
import { GetStartedRoute } from '@components/getStarted/getStartedRoute';

import { Path } from './constants';
import { entitled } from './routeEntitlement';
import {
  useCanConnectDestinationToExistingSource,
  useCanConnectDestinationToNewSource,
  useCanConnectSourceToExistingDestination,
  useCanConnectSourceToNewDestination,
  useCanCreateDestination,
  useCanCreateSource,
  useCanCreateTrackingPlan,
  useCanEditDestinationMapping,
  useCanLinkSourceToTrackingPlan,
  useCanLinkTrackingPlanToSource,
  useCanViewDestinationLiveEvents,
  useCanViewSourceLiveEvents,
  useCanViewTransformationLiveEvents,
  useHasAudiences,
  useHasDataCatalog,
  useHasDataGraph,
  useHasEventMetrics,
  useHasInvoices,
  useHasSqlModels,
} from './entitlements';
import {
  Audiences,
  Canny,
  ChooseTrackingPlan,
  CloneDestination,
  ConnectExistingDestination,
  ConnectExistingSource,
  ConnectNewDestination,
  ConnectNewSource,
  Connections,
  CreateDestination,
  CreateSource,
  CreateTrackingPlans,
  DataCatalog,
  DataGraph,
  DestinationDetails,
  DestinationMappingConfig,
  DestinationsList,
  Directory,
  EventTransformStatusesView,
  EventsView,
  HealthDashboard,
  Invoices,
  LibraryDetailsV2,
  LinkSourceComponent,
  LinkTrackingPlanComponent,
  ModelsList,
  Organization,
  Profile,
  RetlConnectionDetails,
  SourceDetails,
  SourceView,
  SourcesList,
  StatusesView,
  SyncDetail,
  TrialExpiredPage,
  TransformationDetailsV2,
  TransformationsPage,
  WarehouseSyncs,
  Workspace,
} from './pages';

/**
 * The authenticated route table, registered as children of the app-shell layout
 * route in `@auth/appRouter` so the data router matches them directly.
 *
 * Every route is registered unconditionally. Entitlement used to decide whether
 * a route was registered at all, with unentitled URLs falling through to the
 * catch-all `RedirectRoute`; the data router is built exactly once, so the
 * decision now happens inside the route via `entitled(...)`, which renders the
 * same `RedirectRoute` when the check fails. See ./routeEntitlement.
 *
 * `handle` carries the STATIC half of the browser tab title — the section, and
 * the action for pages that act on an entity. The entity's own name is published
 * at render time by the page; see @hooks/useDocumentTitle/entityName. A detail
 * route therefore declares only its section, and reads
 * `Acme Webhook - Sources | RudderStack` once the name resolves.
 */

const transformationListPageContext = definePageContextRoute({
  type: 'transformation.list',
  title: 'Transformations',
  iconKey: 'collect',
});

const transformationDetailsPageContext = definePageContextRoute({
  type: 'transformation.details',
  title: 'Transformation details',
  iconKey: 'collect',
  resources: [subjectResource({ routeParam: 'id', type: 'transformation' })],
});

const transformationLibraryDetailsPageContext = definePageContextRoute({
  type: 'transformationLibrary.details',
  title: 'Transformation library details',
  iconKey: 'collect',
  resources: [subjectResource({ routeParam: 'id', type: 'transformationLibrary' })],
});

export const authorizedRoutes: React.ReactElement[] = [
  <Route
    key={Path.HOME}
    path={Path.HOME}
    element={<Connections />}
    handle={{
      section: SECTION.connections,
      pageContext: definePageContextRoute({
        type: 'connections.list',
        title: 'Connections',
        iconKey: 'home',
      }),
    }}
  />,
  <Route
    key={Path.GET_STARTED}
    path={Path.GET_STARTED}
    element={<GetStartedRoute />}
    handle={{ section: SECTION.getStarted }}
  />,
  <Route
    key={Path.DIRECTORY}
    path={Path.DIRECTORY}
    element={<Directory />}
    handle={{
      section: SECTION.directory,
      pageContext: definePageContextRoute({
        type: 'directory.list',
        title: 'Directory',
        iconKey: 'directory',
      }),
    }}
  />,
  <Route
    key={Path.CANNY}
    path={Path.CANNY}
    element={<Canny />}
    handle={{ section: SECTION.feedback }}
  />,
  <Route
    key={Path.PROFILE}
    path={Path.PROFILE}
    element={<Profile />}
    handle={{ section: SECTION.profileSettings }}
  />,
  <Route
    key={Path.WORKSPACE}
    path={Path.WORKSPACE}
    element={<Workspace />}
    handle={{
      section: SECTION.workspaceSettings,
      pageContext: definePageContextRoute({
        type: 'settings.workspace',
        title: 'Workspace settings',
        iconKey: 'settings',
      }),
    }}
  />,
  <Route
    key={Path.ORGANIZATION}
    path={Path.ORGANIZATION}
    element={<Organization />}
    handle={{
      section: SECTION.organizationSettings,
      pageContext: definePageContextRoute({
        type: 'settings.organization',
        title: 'Organization settings',
        iconKey: 'settings',
      }),
    }}
  />,
  <Route
    key={Path.INVOICES}
    path={Path.INVOICES}
    element={entitled(useHasInvoices, <Invoices />)}
    handle={{ section: SECTION.invoices }}
  />,
  <Route
    key={Path.TRIAL_EXPIRED}
    path={Path.TRIAL_EXPIRED}
    element={<TrialExpiredPage />}
    handle={{ section: SECTION.trialExpired }}
  />,

  // --- Sources --------------------------------------------------------------
  <Route
    key={Path.SOURCES}
    path={Path.SOURCES}
    element={<SourcesList />}
    handle={{
      section: SECTION.sources,
      pageContext: definePageContextRoute({
        type: 'source.list',
        title: 'Sources',
        iconKey: 'collect',
      }),
    }}
  />,
  <Route
    key={Path.CREATE_SOURCE}
    path={Path.CREATE_SOURCE}
    element={entitled(useCanCreateSource, <CreateSource />)}
    handle={{ section: SECTION.sources, action: ACTION.create }}
  />,
  <Route
    key={`${Path.SOURCES}/live/:id`}
    path={`${Path.SOURCES}/live/:id`}
    element={entitled(useCanViewSourceLiveEvents, <EventsView />)}
    handle={{ section: SECTION.sources, action: ACTION.liveEvents }}
  />,
  <Route
    key={Path.CONNECT_EXISTING_DESTINATION(':id')}
    path={Path.CONNECT_EXISTING_DESTINATION(':id')}
    element={entitled(useCanConnectSourceToExistingDestination, <ConnectExistingDestination />)}
    handle={{ section: SECTION.sources, action: ACTION.connectDestination }}
  />,
  <Route
    key={Path.CONNECT_NEW_DESTINATION(':id')}
    path={Path.CONNECT_NEW_DESTINATION(':id')}
    element={entitled(useCanConnectSourceToNewDestination, <ConnectNewDestination />)}
    handle={{ section: SECTION.sources, action: ACTION.connectDestination }}
  />,
  <Route
    key={Path.LINK_TRACKING_PLAN(':id')}
    path={Path.LINK_TRACKING_PLAN(':id')}
    element={entitled(useCanLinkTrackingPlanToSource, <LinkTrackingPlanComponent />)}
    handle={{ section: SECTION.sources, action: ACTION.linkTrackingPlan }}
  />,
  <Route
    key={Path.SOURCE_DETAILS()}
    path={Path.SOURCE_DETAILS()}
    element={<SourceDetails />}
    handle={{
      section: SECTION.sources,
      pageContext: definePageContextRoute({
        type: 'source.details',
        title: 'Source details',
        iconKey: 'collect',
        resources: [subjectResource({ routeParam: 'id', type: 'source' })],
      }),
    }}
  />,

  // --- Destinations ---------------------------------------------------------
  <Route
    key={Path.DESTINATIONS}
    path={Path.DESTINATIONS}
    element={<DestinationsList />}
    handle={{
      section: SECTION.destinations,
      pageContext: definePageContextRoute({
        type: 'destination.list',
        title: 'Destinations',
        iconKey: 'collect',
      }),
    }}
  />,
  <Route
    key={Path.CREATE_DESTINATION}
    path={Path.CREATE_DESTINATION}
    element={entitled(useCanCreateDestination, <CreateDestination />)}
    handle={{ section: SECTION.destinations, action: ACTION.create }}
  />,
  <Route
    key={Path.DESTINATION_LIVE_EVENTS()}
    path={Path.DESTINATION_LIVE_EVENTS()}
    element={entitled(useCanViewDestinationLiveEvents, <StatusesView />)}
    handle={{ section: SECTION.destinations, action: ACTION.liveEvents }}
  />,
  <Route
    key={Path.CLONE_DESTINATION(':id')}
    path={Path.CLONE_DESTINATION(':id')}
    element={entitled(useCanCreateDestination, <CloneDestination />)}
    handle={{ section: SECTION.destinations, action: ACTION.cloneDestination }}
  />,
  <Route
    key={Path.DESTINATION_MAPPING_CONFIG()}
    path={Path.DESTINATION_MAPPING_CONFIG()}
    element={entitled(useCanEditDestinationMapping, <DestinationMappingConfig />)}
    handle={{ section: SECTION.destinations, action: ACTION.destinationMapping }}
  />,
  <Route
    key={Path.CONNECT_EXISTING_SOURCE(':id')}
    path={Path.CONNECT_EXISTING_SOURCE(':id')}
    element={entitled(useCanConnectDestinationToExistingSource, <ConnectExistingSource />)}
    handle={{ section: SECTION.destinations, action: ACTION.connectSource }}
  />,
  <Route
    key={Path.CONNECT_NEW_SOURCE(':destinationId')}
    path={Path.CONNECT_NEW_SOURCE(':destinationId')}
    element={entitled(useCanConnectDestinationToNewSource, <ConnectNewSource />)}
    handle={{ section: SECTION.destinations, action: ACTION.connectSource }}
  />,
  <Route
    key={Path.DESTINATION_DETAILS()}
    path={Path.DESTINATION_DETAILS()}
    element={<DestinationDetails />}
    handle={{
      section: SECTION.destinations,
      pageContext: definePageContextRoute({
        type: 'destination.details',
        title: 'Destination details',
        iconKey: 'collect',
        resources: [subjectResource({ routeParam: 'id', type: 'destination' })],
      }),
    }}
  />,

  // --- Transformations and libraries ---------------------------------------
  <Route
    key={Path.TRANSFORMATIONS}
    path={Path.TRANSFORMATIONS}
    element={<TransformationsPage />}
    handle={{
      section: SECTION.transformations,
      pageContext: transformationListPageContext,
    }}
  />,
  <Route
    key={`${Path.TRANSFORMATIONS}/live/:id`}
    path={`${Path.TRANSFORMATIONS}/live/:id`}
    element={entitled(useCanViewTransformationLiveEvents, <EventTransformStatusesView />)}
    handle={{ section: SECTION.transformations, action: ACTION.liveEvents }}
  />,
  <Route
    key={`${Path.TRANSFORMATIONS}/:id`}
    path={`${Path.TRANSFORMATIONS}/:id`}
    element={<TransformationDetailsV2 />}
    handle={{
      section: SECTION.transformations,
      pageContext: transformationDetailsPageContext,
    }}
  />,
  <Route
    key={`${Path.LIBRARIES}/:id`}
    path={`${Path.LIBRARIES}/:id`}
    element={<LibraryDetailsV2 />}
    handle={{
      section: SECTION.libraries,
      pageContext: transformationLibraryDetailsPageContext,
    }}
  />,

  // --- Models and audiences -------------------------------------------------
  <Route
    key={Path.MODELS}
    path={Path.MODELS}
    element={entitled(useHasSqlModels, <ModelsList />)}
    handle={{
      section: SECTION.models,
      pageContext: definePageContextRoute({
        type: 'retlModel.list',
        title: 'SQL models',
        iconKey: 'activate',
      }),
    }}
  />,
  <Route
    key={Path.MODEL_DETAILS()}
    path={Path.MODEL_DETAILS()}
    element={entitled(useHasSqlModels, <SourceView />)}
    handle={{
      section: SECTION.models,
      pageContext: definePageContextRoute({
        type: 'retlModel.details',
        title: 'SQL model details',
        iconKey: 'activate',
        resources: [subjectResource({ routeParam: 'id', type: 'source' })],
      }),
    }}
  />,
  <Route
    key={Path.AUDIENCES}
    path={Path.AUDIENCES}
    element={entitled(useHasAudiences, <Audiences />)}
    handle={{
      section: SECTION.audiences,
      pageContext: definePageContextRoute({
        type: 'retlAudience.list',
        title: 'rETL audiences',
        iconKey: 'audience-group',
      }),
    }}
  />,
  <Route
    key={Path.AUDIENCE_DETAILS()}
    path={Path.AUDIENCE_DETAILS()}
    element={entitled(useHasAudiences, <SourceView />)}
    handle={{
      section: SECTION.audiences,
      pageContext: definePageContextRoute({
        type: 'retlAudience.details',
        title: 'rETL audience details',
        iconKey: 'audience-group',
        resources: [subjectResource({ routeParam: 'id', type: 'source' })],
      }),
    }}
  />,

  // --- Syncs ----------------------------------------------------------------
  // The old route table rendered this with no props at all, leaving
  // `isDestinationView` undefined; every read of it is a truthiness check, so
  // `false` is the same behaviour, stated explicitly.
  <Route
    key={Path.SYNCS}
    path={Path.SYNCS}
    element={<WarehouseSyncs isDestinationView={false} />}
    handle={{
      section: SECTION.warehouseDestinationSyncs,
      pageContext: definePageContextRoute({
        type: 'sync.list',
        title: 'Syncs',
        iconKey: 'monitor',
      }),
    }}
  />,
  <Route
    key={`${Path.SYNCS}/:id`}
    path={`${Path.SYNCS}/:id`}
    element={<SyncDetail />}
    handle={{
      section: SECTION.warehouseDestinationSyncs,
      pageContext: definePageContextRoute({
        type: 'sync.details',
        title: 'Sync details',
        iconKey: 'monitor',
        resources: [subjectResource({ routeParam: 'id', type: 'sync' })],
      }),
    }}
  />,

  // --- Tracking plans -------------------------------------------------------
  <Route
    key={Path.SETUP_TRACKING_PLAN}
    path={Path.SETUP_TRACKING_PLAN}
    element={<ChooseTrackingPlan />}
    handle={{ section: SECTION.trackingPlans, action: ACTION.create }}
  />,
  <Route
    key={Path.TRACKING_PLANS_SETUP(':mode')}
    path={Path.TRACKING_PLANS_SETUP(':mode')}
    element={entitled(useCanCreateTrackingPlan, <CreateTrackingPlans />)}
    handle={{ section: SECTION.trackingPlans, action: ACTION.create }}
  />,
  <Route
    key={Path.LINK_NEW_SOURCE(':id')}
    path={Path.LINK_NEW_SOURCE(':id')}
    element={entitled(useCanLinkSourceToTrackingPlan, <LinkSourceComponent />)}
    handle={{ section: SECTION.trackingPlans, action: ACTION.linkSource }}
  />,

  // --- Everything else ------------------------------------------------------
  <Route
    key={Path.HEALTH_DASHBOARD}
    path={Path.HEALTH_DASHBOARD}
    element={entitled(useHasEventMetrics, <HealthDashboard />)}
    handle={{
      section: SECTION.healthDashboard,
      pageContext: definePageContextRoute({
        type: 'health.overview',
        title: 'Health',
        iconKey: 'monitor',
      }),
    }}
  />,
  <Route
    key={Path.DATA_CATALOG}
    path={Path.DATA_CATALOG}
    element={entitled(useHasDataCatalog, <DataCatalog />)}
    handle={{
      section: SECTION.dataCatalog,
      pageContext: definePageContextRoute({
        type: 'dataCatalog.overview',
        title: 'Data catalog',
        iconKey: 'govern',
      }),
    }}
  />,
  <Route
    key={Path.CONNECTION_DETAILS(':id')}
    path={Path.CONNECTION_DETAILS(':id')}
    element={<RetlConnectionDetails />}
    handle={{
      section: SECTION.retlConnections,
      pageContext: definePageContextRoute({
        type: 'retlConnection.details',
        title: 'Reverse ETL connection details',
        iconKey: 'activate',
        resources: [subjectResource({ routeParam: 'id', type: 'retlConnection' })],
      }),
    }}
  />,
  <Route
    key={Path.DATA_GRAPH}
    path={Path.DATA_GRAPH}
    element={entitled(useHasDataGraph, <DataGraph />)}
    handle={{
      section: SECTION.dataGraph,
      pageContext: definePageContextRoute({
        type: 'dataGraph.overview',
        title: 'Data graph',
        iconKey: 'user-entity',
      }),
    }}
  />,

  // --- Feature areas that nest their own routes -----------------------------
  // Each owns its subtree, including the store providers that wrap it (now
  // layout routes) and its own not-found redirect.
  botManagementRoutes,
  eventBlockingRoutes,
  dataMapperRoutes,
  trackingPlansRoutes,
  audienceCohortRoutes,
  dataGraphAudienceRoutes,
  profilesRoutes,
  accessManagementRoutes,
  ...retlWorkflowRoutes,
];
